import {
  conversationSchema,
  myAnnouncementsSchema,
  myStreamerSchema,
  portalMeSchema,
  streamerChecklistSchema,
  type PortalMe,
  streamerDashboardSchema,
  wallSchema,
  type ChecklistEntry,
  type Conversation,
  type DailyPoint,
  type MyAnnouncements,
  type MyPadrinho,
  type MyStreamer,
  type PublicLink,
  type StreamerChecklist,
  type StreamerDashboard,
  type Wall,
} from '@growup/streaming-shared';
import { Button, Checkbox, Table, Textarea, type Column } from '@growup/ui';
import { useEffect, useState, type ReactNode } from 'react';
import { Navigate, useSearchParams } from 'react-router-dom';

import { getJson, messageOf, send } from './api';
import { ChannelCard } from './ChannelCard';
import { HistoryCard } from './HistoryCard';
import { ChecklistCard, daysUntil, markChecklistEntry, nextEntries } from './ChecklistCard';
import { formatChange, formatCount, formatDate, formatDateTime, formatMinutes } from './format';
import { siteLink, usePortal } from './portal';
import { Card, ErrorNote, Field, Notice, PageTitle, ProgressBar } from './ui';

const PERIODS = [7, 30, 90] as const;

/**
 * O painel em separadores curtos (arrumar o painel, parte 1): Início (um
 * relance), Primeiros 30 dias (enquanto a checklist esta aberta), Anúncios,
 * Padrinho (cartao + conversa), Mural (com padrinho), Números e O meu canal.
 * O separador fica no endereco (`?ver=`); o antigo `?ver=equipa` abre o Início.
 */
const TAB_LABELS = {
  inicio: 'Início',
  checklist: 'Primeiros 30 dias',
  anuncios: 'Anúncios',
  padrinho: 'Padrinho',
  mural: 'Mural',
  numeros: 'Números',
  canal: 'O meu canal',
} as const;
type PanelTab = keyof typeof TAB_LABELS;

function PanelTabs({
  tabs,
  tab,
  onChange,
}: {
  tabs: { id: PanelTab; count: number }[];
  tab: PanelTab;
  onChange: (tab: PanelTab) => void;
}) {
  return (
    <div
      role="tablist"
      aria-label="Painel"
      className="flex gap-1 overflow-x-auto border-b border-gu-line"
    >
      {tabs.map((t) => (
        <button
          key={t.id}
          id={`separador-${t.id}`}
          type="button"
          role="tab"
          aria-selected={tab === t.id}
          aria-controls={`painel-${t.id}`}
          className="-mb-px inline-flex shrink-0 items-center gap-2 border-b-2 border-transparent px-4 py-2 font-sub text-label font-bold uppercase text-gu-ink-muted hover:text-gu-ink aria-selected:border-gu-accent aria-selected:text-gu-ink"
          onClick={() => {
            onChange(t.id);
          }}
        >
          {TAB_LABELS[t.id]}
          {t.count > 0 ? (
            <span
              className="rounded-full bg-gu-accent px-1.5 text-caption leading-5 text-gu-on-accent"
              aria-label={`(${String(t.count)})`}
            >
              {t.count}
            </span>
          ) : null}
        </button>
      ))}
    </div>
  );
}

/** `/painel`: o painel do streamer, em separadores. */
export function Dashboard() {
  const portal = usePortal();
  const [streamer, setStreamer] = useState<'loading' | 'no' | MyStreamer>('loading');
  const [me, setMe] = useState<PortalMe | null>(null);
  const [days, setDays] = useState<number>(30);
  const [data, setData] = useState<StreamerDashboard | null>(null);
  const [checklist, setChecklist] = useState<StreamerChecklist | null>(null);
  const [announcements, setAnnouncements] = useState<MyAnnouncements | null>(null);
  const [wall, setWall] = useState<Wall | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [params, setParams] = useSearchParams();

  useEffect(() => {
    getJson('/api/me', portalMeSchema)
      .then((m) => {
        setMe(m);
        setStreamer(m.streamer ?? 'no');
      })
      .catch((e: unknown) => setError(messageOf(e)));
  }, []);

  const isStreamer = streamer !== 'loading' && streamer !== 'no';
  const hasPadrinho = me?.padrinho != null;

  // O que os separadores e o Inicio contam: lido uma vez, partilhado pelos cartoes.
  useEffect(() => {
    if (!isStreamer) return;
    getJson('/api/me/checklist', streamerChecklistSchema)
      .then(setChecklist)
      .catch((e: unknown) => setError(messageOf(e)));
    getJson('/api/me/announcements', myAnnouncementsSchema)
      .then(setAnnouncements)
      .catch((e: unknown) => setError(messageOf(e)));
  }, [isStreamer]);

  useEffect(() => {
    if (!isStreamer || !hasPadrinho) return;
    getJson('/api/me/wall', wallSchema)
      .then(setWall)
      .catch((e: unknown) => setError(messageOf(e)));
  }, [isStreamer, hasPadrinho]);

  useEffect(() => {
    if (!isStreamer) return;
    getJson(`/api/me/dashboard?days=${String(days)}`, streamerDashboardSchema)
      .then(setData)
      .catch((e: unknown) => setError(messageOf(e)));
  }, [isStreamer, days]);

  if (streamer === 'no') return <Navigate to="/candidatura" replace />;

  const checklistOpen = checklist !== null && checklist.closedAt === null;
  const tabs: { id: PanelTab; count: number }[] = [
    { id: 'inicio', count: 0 },
    ...(checklistOpen
      ? [{ id: 'checklist' as const, count: checklist.total - checklist.done }]
      : []),
    { id: 'anuncios', count: announcements?.unread ?? 0 },
    { id: 'padrinho', count: me?.padrinho?.unreadMessages ?? 0 },
    ...(hasPadrinho ? [{ id: 'mural' as const, count: wall?.unread ?? 0 }] : []),
    { id: 'numeros', count: 0 },
    { id: 'canal', count: 0 },
  ];
  const wanted = params.get('ver');
  const tab: PanelTab = tabs.find((t) => t.id === wanted)?.id ?? 'inicio';
  const setTab = (next: PanelTab) => {
    setParams(next === 'inicio' ? {} : { ver: next }, { replace: true });
  };

  const markEntry = async (entry: ChecklistEntry) => {
    setError(null);
    try {
      setChecklist(await markChecklistEntry(entry));
    } catch (e) {
      setError(messageOf(e));
    }
  };

  const panel = (id: PanelTab, children: ReactNode) =>
    tab === id ? (
      <div
        role="tabpanel"
        id={`painel-${id}`}
        aria-labelledby={`separador-${id}`}
        className="flex flex-col gap-6"
      >
        {children}
      </div>
    ) : null;

  return (
    <div className="flex flex-col gap-6">
      <PageTitle lead={isStreamer ? `Olá, ${streamer.displayName}.` : undefined}>
        O meu painel
      </PageTitle>
      <ErrorNote message={error} />
      {isStreamer ? <PanelTabs tabs={tabs} tab={tab} onChange={setTab} /> : null}
      {isStreamer
        ? panel(
            'inicio',
            <HomeTab
              me={me}
              checklist={checklistOpen ? checklist : null}
              announcements={announcements}
              data={data}
              days={days}
              onTab={setTab}
              onMark={(e) => void markEntry(e)}
            />,
          )
        : null}
      {isStreamer && checklistOpen
        ? panel('checklist', <ChecklistCard data={checklist} onMark={(e) => void markEntry(e)} />)
        : null}
      {isStreamer
        ? panel('anuncios', <AnnouncementsCard data={announcements} onChange={setAnnouncements} />)
        : null}
      {isStreamer && me
        ? panel(
            'padrinho',
            <>
              <PadrinhoCard me={me} onChanged={setMe} />
              {me.padrinho ? (
                <MessagesCard
                  padrinho={me.padrinho}
                  onRead={() =>
                    setMe((m) =>
                      m?.padrinho ? { ...m, padrinho: { ...m.padrinho, unreadMessages: 0 } } : m,
                    )
                  }
                />
              ) : null}
            </>,
          )
        : null}
      {isStreamer && hasPadrinho
        ? panel('mural', <WallCard wall={wall} onChange={setWall} />)
        : null}
      {isStreamer
        ? panel(
            'numeros',
            <>
              {portal && !portal.twitch ? (
                <Notice title="Números da Twitch por ligar">
                  A Grow uP ainda está a configurar a ligação à Twitch. O painel enche-se sozinho
                  quando estiver pronta.
                </Notice>
              ) : null}
              <PeriodPicker days={days} onChange={setDays} />
              {data ? <StreamerStats data={data} /> : null}
              <HistoryCard />
            </>,
          )
        : null}
      {isStreamer
        ? panel(
            'canal',
            <>
              {streamer.status === 'active' ? <ChannelCard /> : null}
              <PublicProfileCard streamer={streamer} onSaved={setStreamer} />
            </>,
          )
        : null}
    </div>
  );
}

/** Um cartao do Inicio: titulo pequeno, numero grande, uma linha e o caminho. */
function HomeTile({
  title,
  value,
  note,
  action,
  onClick,
}: {
  title: string;
  value: string;
  note: ReactNode;
  action: string;
  onClick: () => void;
}) {
  return (
    <section className="flex flex-col gap-1 rounded-lg border border-gu-line bg-gu-raised p-4">
      <h2 className="font-sub text-label font-bold uppercase text-gu-ink-muted">{title}</h2>
      <p className="font-display text-headline">{value}</p>
      <div className="flex-1 text-caption text-gu-ink-muted">{note}</div>
      <div>
        <button type="button" className="gu-link text-caption" onClick={onClick}>
          {action}
        </button>
      </div>
    </section>
  );
}

/**
 * O Inicio: tudo o que importa num relance (checklist, padrinho, anuncios,
 * numeros), o proximo passo da checklist e o ultimo anuncio.
 */
function HomeTab({
  me,
  checklist,
  announcements,
  data,
  days,
  onTab,
  onMark,
}: {
  me: PortalMe | null;
  checklist: StreamerChecklist | null;
  announcements: MyAnnouncements | null;
  data: StreamerDashboard | null;
  days: number;
  onTab: (tab: PanelTab) => void;
  onMark: (entry: ChecklistEntry) => void;
}) {
  const next = checklist ? nextEntries(checklist, 2) : [];
  const latest = announcements?.items[0] ?? null;
  const padrinho = me?.padrinho ?? null;
  return (
    <>
      <div className="grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-4">
        {checklist ? (
          <HomeTile
            title="Primeiros 30 dias"
            value={`${String(checklist.done)} / ${String(checklist.total)}`}
            note={
              <>
                <ProgressBar done={checklist.done} total={checklist.total} />
                {`Faltam ${String(daysUntil(checklist.dueOn))} dias`}
              </>
            }
            action="Ver o que falta"
            onClick={() => onTab('checklist')}
          />
        ) : null}
        <HomeTile
          title="Padrinho"
          value={padrinho ? padrinho.login : 'Por atribuir'}
          note={
            padrinho
              ? padrinho.unreadMessages > 0
                ? `${String(padrinho.unreadMessages)} ${padrinho.unreadMessages === 1 ? 'mensagem nova' : 'mensagens novas'}`
                : 'Sem mensagens novas'
              : 'O Stream Staff atribui-te um em breve.'
          }
          action={padrinho ? 'Abrir conversa' : 'Ver mais'}
          onClick={() => onTab('padrinho')}
        />
        <HomeTile
          title="Anúncios"
          value={
            announcements && announcements.unread > 0
              ? `${String(announcements.unread)} ${announcements.unread === 1 ? 'novo' : 'novos'}`
              : 'Em dia'
          }
          note={latest ? `«${latest.title}»` : 'Ainda não há anúncios.'}
          action="Ver anúncios"
          onClick={() => onTab('anuncios')}
        />
        <HomeTile
          title={`Últimos ${String(days)} dias`}
          value={data ? `${formatCount(data.totals.sessions)} lives` : '—'}
          note={
            data
              ? `${formatMinutes(data.totals.minutesLive)} em direto · ${formatCount(data.totals.avgViewers)} espectadores em média`
              : 'À espera dos números da Twitch.'
          }
          action="Ver números"
          onClick={() => onTab('numeros')}
        />
      </div>
      {next.length > 0 || latest ? (
        <div className="grid gap-6 lg:grid-cols-3">
          {next.length > 0 ? (
            <div className="lg:col-span-2">
              <Card title="O teu próximo passo">
                <ul className="flex flex-col gap-2">
                  {next.map((e) => (
                    <li
                      key={e.itemId}
                      className="flex flex-wrap items-center justify-between gap-2 rounded-md bg-gu-sunken px-3 py-2"
                    >
                      <span className="flex flex-col">
                        <span className="font-semibold">{e.title}</span>
                        {e.help ? (
                          <span className="text-caption text-gu-ink-muted">{e.help}</span>
                        ) : null}
                      </span>
                      {e.markedBy === 'streamer' ? (
                        <Button
                          size="sm"
                          aria-label={`Feito: ${e.title}`}
                          onClick={() => onMark(e)}
                        >
                          Feito
                        </Button>
                      ) : (
                        <span className="text-caption text-gu-ink-muted">
                          {e.markedBy === 'padrinho' ? 'O padrinho marca' : 'Marca-se sozinho'}
                        </span>
                      )}
                    </li>
                  ))}
                </ul>
              </Card>
            </div>
          ) : null}
          {latest ? (
            <Card title="Último anúncio">
              <div className="flex flex-col gap-2">
                <p className="font-semibold">{latest.title}</p>
                <p className="line-clamp-4 whitespace-pre-line text-gu-ink-muted">{latest.body}</p>
                <div>
                  <Button variant="secondary" size="sm" onClick={() => onTab('anuncios')}>
                    Ver anúncios
                  </Button>
                </div>
              </div>
            </Card>
          ) : null}
        </div>
      ) : null}
    </>
  );
}

/** Mensagens privadas com o padrinho (GS13). Abrir a conversa marca-a como lida. */
function MessagesCard({ padrinho, onRead }: { padrinho: MyPadrinho; onRead: () => void }) {
  const [conversation, setConversation] = useState<Conversation | null>(null);
  const [body, setBody] = useState('');
  const [error, setError] = useState<string | null>(null);

  // O separador Padrinho abre a conversa logo (sem botao "Abrir conversa");
  // abri-la marca-a como lida.
  useEffect(() => {
    getJson('/api/me/messages', conversationSchema)
      .then((c) => {
        setConversation(c);
        onRead();
      })
      .catch((e: unknown) => setError(messageOf(e)));
  }, []);
  const submit = async () => {
    setError(null);
    try {
      setConversation(await send('POST', '/api/me/messages', { body }, conversationSchema));
      setBody('');
    } catch (e) {
      setError(messageOf(e));
    }
  };

  return (
    <Card title={`Mensagens com ${padrinho.login}`}>
      <div className="flex flex-col gap-4">
        <p className="text-gu-ink-muted">
          Uma conversa só tua com o teu padrinho. O Stream Staff pode lê-la se for preciso.
        </p>
        <ErrorNote message={error} />
        {conversation && conversation.messages.length === 0 ? (
          <p className="text-gu-ink-muted">Ainda não há mensagens. Diz olá!</p>
        ) : null}
        <ul className="flex flex-col gap-2 border-l-2 border-gu-line pl-3">
          {conversation?.messages.map((m) => (
            <li key={m.id} className="flex flex-col">
              <span className="text-caption text-gu-ink-muted">
                {m.from === 'me' ? 'Eu' : m.author} · {formatDateTime(m.createdAt)}
              </span>
              <span className="whitespace-pre-line">{m.body}</span>
            </li>
          ))}
        </ul>
        <Field label="Mensagem">
          <Textarea value={body} maxLength={2000} onChange={(e) => setBody(e.target.value)} />
        </Field>
        <div>
          <Button disabled={body.trim() === ''} onClick={() => void submit()}>
            Enviar
          </Button>
        </div>
      </div>
    </Card>
  );
}

function WallCard({ wall, onChange: setWall }: { wall: Wall | null; onChange: (w: Wall) => void }) {
  const [body, setBody] = useState('');
  const [error, setError] = useState<string | null>(null);

  const act = async (run: () => Promise<Wall>) => {
    setError(null);
    try {
      setWall(await run());
      return true;
    } catch (e) {
      setError(messageOf(e));
      return false;
    }
  };

  return (
    <Card
      title={
        wall && wall.unread > 0
          ? `Mural do grupo · ${String(wall.unread)} por ler`
          : 'Mural do grupo'
      }
      actions={
        wall && wall.unread > 0 ? (
          <Button
            variant="secondary"
            size="sm"
            onClick={() => void act(() => send('POST', '/api/me/wall/seen', {}, wallSchema))}
          >
            Marcar como lido
          </Button>
        ) : undefined
      }
    >
      <div className="flex flex-col gap-4">
        <p className="text-gu-ink-muted">
          O teu grupo: {wall?.padrinho.login ?? 'o teu padrinho'} e os afilhados. Partilha dúvidas,
          ideias e o que estás a fazer.
        </p>
        <Field label="Escrever no mural">
          <Textarea value={body} maxLength={2000} onChange={(e) => setBody(e.target.value)} />
        </Field>
        <div>
          <Button
            disabled={body.trim() === ''}
            onClick={() =>
              void act(() => send('POST', '/api/me/wall', { body }, wallSchema)).then(
                (ok) => ok && setBody(''),
              )
            }
          >
            Publicar
          </Button>
        </div>
        <ErrorNote message={error} />
        {wall && wall.posts.length === 0 ? (
          <p className="text-gu-ink-muted">O mural ainda está vazio.</p>
        ) : null}
        <ul className="flex flex-col gap-5">
          {wall?.posts.map((p) => (
            <WallPostItem
              key={p.id}
              post={p}
              onComment={(text) =>
                act(() => send('POST', `/api/me/wall/${p.id}/comments`, { body: text }, wallSchema))
              }
            />
          ))}
        </ul>
      </div>
    </Card>
  );
}

function WallPostItem({
  post: p,
  onComment,
}: {
  post: Wall['posts'][number];
  onComment: (body: string) => Promise<boolean>;
}) {
  const [comment, setComment] = useState('');
  const who = (author: string, role: string) =>
    role === 'padrinho'
      ? `${author} (padrinho)`
      : role === 'staff'
        ? `${author} (Stream Staff)`
        : author;
  return (
    <li className="flex flex-col gap-2">
      <p className="text-caption text-gu-ink-muted">
        {p.unread ? <span className="text-gu-accent">Novo · </span> : null}
        {who(p.author, p.role)} · {formatDateTime(p.createdAt)}
      </p>
      <p className="whitespace-pre-line">{p.body}</p>
      <ul className="flex flex-col gap-2 border-l-2 border-gu-line pl-3">
        {p.comments.map((c) => (
          <li key={c.id}>
            <span className="text-caption text-gu-ink-muted">
              {who(c.author, c.role)} · {formatDateTime(c.createdAt)}
            </span>
            <p className="whitespace-pre-line">{c.body}</p>
          </li>
        ))}
      </ul>
      <div className="flex flex-wrap items-end gap-2">
        <Field label="Comentário">
          <Textarea
            value={comment}
            rows={2}
            maxLength={1000}
            onChange={(e) => setComment(e.target.value)}
          />
        </Field>
        <Button
          variant="secondary"
          size="sm"
          disabled={comment.trim() === ''}
          onClick={() => void onComment(comment).then((ok) => ok && setComment(''))}
        >
          Comentar
        </Button>
      </div>
    </li>
  );
}

/** Team announcements (GS12): the unread ones stay marked until the streamer says so. */
function AnnouncementsCard({
  data,
  onChange: setData,
}: {
  data: MyAnnouncements | null;
  onChange: (d: MyAnnouncements) => void;
}) {
  const [error, setError] = useState<string | null>(null);

  const markSeen = async () => {
    try {
      setData(await send('POST', '/api/me/announcements/seen', {}, myAnnouncementsSchema));
    } catch (e) {
      setError(messageOf(e));
    }
  };

  if (data === null) return null;
  return (
    <Card
      title={
        data && data.unread > 0
          ? `Anúncios da equipa · ${String(data.unread)} por ler`
          : 'Anúncios da equipa'
      }
      actions={
        data && data.unread > 0 ? (
          <Button variant="secondary" size="sm" onClick={() => void markSeen()}>
            Marcar como lidos
          </Button>
        ) : undefined
      }
    >
      <ErrorNote message={error} />
      {data && data.items.length === 0 ? (
        <p className="text-gu-ink-muted">Ainda não há anúncios da equipa.</p>
      ) : null}
      <ul className="flex flex-col gap-4">
        {data?.items.map((a) => (
          <li key={a.id} className="flex flex-col gap-1">
            <p className="font-semibold">
              {a.unread ? <span className="text-gu-accent">Novo · </span> : null}
              {a.title}
            </p>
            <p className="text-caption text-gu-ink-muted">
              {a.author} · {formatDateTime(a.createdAt)}
              {a.padrinho ? ' · para os afilhados do teu padrinho' : ''}
            </p>
            <p className="whitespace-pre-line">{a.body}</p>
          </li>
        ))}
      </ul>
    </Card>
  );
}

/**
 * The streamer's padrinho or madrinha (GS1), and asking the Stream Staff for
 * another one.
 */
function PadrinhoCard({ me, onChanged }: { me: PortalMe; onChanged: (me: PortalMe) => void }) {
  const [asking, setAsking] = useState(false);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const ask = async () => {
    setBusy(true);
    setError(null);
    try {
      onChanged(await send('POST', '/api/me/padrinho-request', { reason }, portalMeSchema));
      setAsking(false);
    } catch (e) {
      setError(messageOf(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card title="O teu padrinho na equipa">
      <div className="flex flex-col gap-3">
        <p>
          {me.padrinho
            ? `${me.padrinho.login} é o teu padrinho ou madrinha${me.padrinho.confirmed ? '' : ' (provisório até à reunião mensal do Stream Staff)'}. É a pessoa a quem podes perguntar tudo.`
            : 'Ainda não tens padrinho ou madrinha: o Stream Staff atribui-te um em breve.'}
        </p>
        {me.padrinhoRequest ? (
          <Notice title="Pedido enviado">
            Pediste {me.padrinho ? 'outro padrinho' : 'um padrinho'} a{' '}
            {formatDate(me.padrinhoRequest.at)}. O Stream Staff vai tratar disso.
          </Notice>
        ) : asking ? (
          <>
            <Field
              label={me.padrinho ? 'Porque queres outro padrinho' : 'Conta-nos o que precisas'}
              hint="Só o Stream Staff lê."
            >
              <Textarea
                value={reason}
                maxLength={1000}
                onChange={(e) => setReason(e.target.value)}
              />
            </Field>
            <ErrorNote message={error} />
            <div className="flex flex-wrap gap-2">
              <Button disabled={busy || reason.trim().length < 3} onClick={() => void ask()}>
                Enviar pedido
              </Button>
              <Button variant="ghost" onClick={() => setAsking(false)}>
                Cancelar
              </Button>
            </div>
          </>
        ) : (
          <div>
            <Button variant="secondary" onClick={() => setAsking(true)}>
              {me.padrinho ? 'Pedir outro padrinho' : 'Pedir um padrinho'}
            </Button>
          </div>
        )}
      </div>
    </Card>
  );
}

const LINK_LABELS: Record<PublicLink, string> = {
  youtube: 'YouTube',
  instagram: 'Instagram',
  x: 'X',
};

/** What the streamer shows on growup.gg/streamers: the account photo and their networks. */
function PublicProfileCard({
  streamer,
  onSaved,
}: {
  streamer: MyStreamer;
  onSaved: (s: MyStreamer) => void;
}) {
  const portal = usePortal();
  const [showPhoto, setShowPhoto] = useState(streamer.showPhoto);
  const [links, setLinks] = useState<PublicLink[]>(streamer.showLinks);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const page = siteLink(portal, '/streamers');
  const networks = siteLink(portal, '/conta/redes');

  const toggle = (link: PublicLink, on: boolean) =>
    setLinks((current) => (on ? [...current, link] : current.filter((l) => l !== link)));

  const save = async () => {
    setBusy(true);
    setMessage(null);
    try {
      onSaved(
        await send(
          'PUT',
          '/api/me/public-profile',
          { showPhoto, showLinks: links },
          myStreamerSchema,
        ),
      );
      setMessage('Guardado.');
    } catch (e) {
      setMessage(messageOf(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card title="A tua página pública">
      <div className="flex flex-col gap-3">
        <p className="text-gu-ink-muted">
          O que aparece no teu cartão em{' '}
          {page ? (
            <a className="gu-link" href={page}>
              growup.gg/streamers
            </a>
          ) : (
            'growup.gg/streamers'
          )}
          . Sem a foto de perfil, aparece a imagem do teu canal na Twitch.
        </p>
        <Checkbox
          label="Mostrar a minha foto de perfil da conta Grow uP"
          checked={showPhoto}
          onChange={(e) => setShowPhoto(e.target.checked)}
        />
        {streamer.photoHidden ? (
          <p className="text-caption text-gu-ink-muted">
            A equipa de streaming escondeu a tua foto. Fala com ela se quiseres que volte.
          </p>
        ) : null}
        <fieldset className="flex flex-col gap-2">
          <legend className="mb-1 text-caption text-gu-ink-muted">
            Redes a mostrar, além da Twitch (só as que tens ligadas{' '}
            {networks ? (
              <a className="gu-link" href={networks}>
                na tua conta
              </a>
            ) : (
              'na tua conta'
            )}
            ):
          </legend>
          {(Object.keys(LINK_LABELS) as PublicLink[]).map((link) => (
            <Checkbox
              key={link}
              label={LINK_LABELS[link]}
              checked={links.includes(link)}
              onChange={(e) => toggle(link, e.target.checked)}
            />
          ))}
        </fieldset>
        <div className="flex flex-wrap items-center gap-3">
          <Button size="sm" disabled={busy} onClick={() => void save()}>
            Guardar
          </Button>
          {message ? <span className="text-caption text-gu-ink-muted">{message}</span> : null}
        </div>
      </div>
    </Card>
  );
}

export function PeriodPicker({ days, onChange }: { days: number; onChange: (d: number) => void }) {
  return (
    <div className="flex gap-1" role="group" aria-label="Período">
      {PERIODS.map((d) => (
        <button
          key={d}
          type="button"
          aria-pressed={days === d}
          onClick={() => onChange(d)}
          className="border-b-2 border-transparent px-3 py-2 font-sub text-label font-bold uppercase text-gu-ink-muted hover:text-gu-ink aria-pressed:border-gu-accent aria-pressed:text-gu-ink"
        >
          {d} dias
        </button>
      ))}
    </div>
  );
}

const SESSION_COLUMNS: Column<StreamerDashboard['sessions'][number]>[] = [
  {
    key: 'when',
    header: 'Início',
    cell: (s) => formatDateTime(s.startedAt),
  },
  {
    key: 'what',
    header: 'Transmissão',
    cell: (s) => (
      <span className="flex flex-col">
        <span>{s.title ?? '—'}</span>
        {s.game ? <span className="text-caption text-gu-ink-muted">{s.game}</span> : null}
      </span>
    ),
  },
  {
    key: 'minutes',
    header: 'Duração',
    align: 'right',
    cell: (s) => (s.endedAt === null ? 'no ar' : formatMinutes(s.minutes)),
  },
  {
    key: 'viewers',
    header: 'Média · pico',
    align: 'right',
    hideOnMobile: true,
    cell: (s) => `${formatCount(s.avgViewers)} · ${formatCount(s.peakViewers)}`,
  },
];

/** Tiles, the daily chart and the list of broadcasts. */
export function StreamerStats({ data }: { data: StreamerDashboard }) {
  const t = data.totals;
  return (
    <>
      {data.live ? (
        <Notice title="Estás em direto">
          {data.live.title} · {formatCount(data.live.viewers)} a ver
        </Notice>
      ) : null}
      <ul className="grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-4">
        <Tile
          label="Em direto"
          value={formatMinutes(t.minutesLive)}
          note={`${String(t.sessions)} transmissões`}
        />
        <Tile
          label="Espectadores (média)"
          value={formatCount(t.avgViewers)}
          note={`pico ${formatCount(t.peakViewers)}`}
        />
        <Tile
          label="Seguidores"
          value={t.followers === null ? '—' : formatCount(t.followers)}
          note={
            t.followersChange === null
              ? 'sem histórico ainda'
              : `${formatChange(t.followersChange)} no período`
          }
        />
        <Tile
          label="Última leitura"
          value={data.syncedAt ? formatDateTime(data.syncedAt) : '—'}
          note={`${formatDate(data.from)} a ${formatDate(data.to)}`}
        />
      </ul>
      <Card title="Minutos em direto por dia">
        <DailyBars points={data.daily} />
      </Card>
      <Card title="Transmissões">
        <Table
          caption="Transmissões do período"
          columns={SESSION_COLUMNS}
          rows={data.sessions}
          rowKey={(s) => s.startedAt}
          empty="Sem transmissões neste período."
        />
      </Card>
    </>
  );
}

function Tile({ label, value, note }: { label: string; value: string; note: string }) {
  return (
    <li className="flex flex-col gap-1 border-t-2 border-gu-accent bg-gu-raised p-4">
      <span className="font-sub text-label font-bold uppercase text-gu-ink-muted">{label}</span>
      <span className="font-display text-headline text-gu-ink">{value}</span>
      <span className="text-caption text-gu-ink-muted">{note}</span>
    </li>
  );
}

/**
 * One bar per day (single series, brand accent), anchored to the baseline with
 * rounded tops and a 2px gap. Each bar has its own tooltip and label; the
 * broadcasts table below is the table view.
 */
export function DailyBars({ points }: { points: DailyPoint[] }) {
  const max = Math.max(1, ...points.map((p) => p.minutesLive));
  const total = points.reduce((sum, p) => sum + p.minutesLive, 0);
  if (total === 0) {
    return <p className="text-body text-gu-ink-muted">Ainda não há transmissões neste período.</p>;
  }
  return (
    <div className="flex flex-col gap-2">
      <div className="flex h-40 items-end gap-[2px] border-b border-gu-line" role="list">
        {points.map((p) => {
          const label = `${formatDate(p.date)}: ${formatMinutes(p.minutesLive)}${
            p.peakViewers > 0 ? `, pico ${formatCount(p.peakViewers)}` : ''
          }`;
          return (
            <div
              key={p.date}
              role="listitem"
              aria-label={label}
              title={label}
              className="group flex h-full flex-1 items-end hover:bg-gu-ink/5"
            >
              <div
                className="w-full rounded-t bg-gu-accent group-hover:opacity-80"
                style={{ height: `${String((p.minutesLive / max) * 100)}%` }}
              />
            </div>
          );
        })}
      </div>
      <div className="flex justify-between text-caption text-gu-ink-muted">
        <span>{formatDate(points[0]?.date ?? '')}</span>
        <span>máx. {formatMinutes(max)}</span>
        <span>{formatDate(points[points.length - 1]?.date ?? '')}</span>
      </div>
    </div>
  );
}
